import { t as localeText } from "@/shared/lib/translate";
import { CloseIcon } from "./icons";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { Badge } from "./Badge";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Every reference attachment in every workflow is this square. Image, video,
 * and audio composers share the size, radius, and hover treatment so the
 * attachment row never changes shape when the creator switches media.
 */
export const REFERENCE_TILE_SIZE_CLASS = "size-[64px]";

export function referenceTileClass(
  disabled: boolean,
  expanded?: boolean,
): string {
  return cn(
    REFERENCE_TILE_SIZE_CLASS,
    "studio-reference-tile",
    "flex shrink-0 flex-col items-center justify-center gap-1.5 rounded-[10px] border-dashed p-1.5 text-center font-sans text-[11px] font-medium leading-tight text-ink-soft outline-none transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-house focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] motion-reduce:transition-none",
    disabled
      ? "cursor-not-allowed text-ink-faint opacity-60"
      : "cursor-pointer hover:bg-hover",
    expanded && "bg-[var(--selection-bg)] text-ink",
  );
}

export interface ReferenceTileButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "disabled"> {
  icon: ReactNode;
  label: string;
  disabled?: boolean;
  expanded?: boolean;
}

/** The dashed "add a reference" square. */
export function ReferenceTileButton({
  icon,
  label,
  disabled = false,
  expanded,
  onClick,
  className,
  ...props
}: ReferenceTileButtonProps) {
  return (
    <Button
      variant="outline"
      aria-disabled={disabled || undefined}
      aria-haspopup={disabled ? undefined : "menu"}
      aria-expanded={disabled ? undefined : expanded}
      onClick={(event) => {
        if (disabled) return;
        onClick?.(event);
      }}
      className={cn(referenceTileClass(disabled, expanded), className)}
      {...props}
    >
      {icon}
      <span>{label}</span>
    </Button>
  );
}

export interface AttachedReferenceTileProps {
  /** Thumbnail source; falls back to the placeholder icon when absent. */
  src?: string;
  alt: string;
  /** Compact image identifier shown below the unobscured thumbnail. */
  tag?: string;
  /** Optional role or context retained as a native tooltip. */
  title?: string;
  placeholder?: ReactNode;
  removeLabel?: string;
  onRemove?: () => void;
}

/** A 64px attachment square with optional image tag below the thumbnail. */
export function AttachedReferenceTile({
  src,
  alt,
  tag,
  title,
  placeholder,
  removeLabel,
  onRemove,
}: AttachedReferenceTileProps) {
  return (
    <figure className="group flex w-[64px] shrink-0 flex-col items-center gap-1" title={title ?? alt}>
      <div
        className={cn(
          REFERENCE_TILE_SIZE_CLASS,
          "relative overflow-hidden rounded-[10px] border border-line-strong bg-canvas-soft",
        )}
      >
        {src ? (
          <img src={src} alt={alt} className="size-full object-cover" />
        ) : (
          <span
            aria-label={alt}
            role="img"
            className="grid size-full place-items-center bg-well text-ink-faint"
          >
            {placeholder}
          </span>
        )}
        {onRemove ? (
          <IconButton
            tone="glass"
            size="sm"
            aria-label={removeLabel ?? localeText("interface.remove_value0_4945934f", { defaultValue: "Remove {{value0}}", value0: alt })}
            onClick={onRemove}
            className="studio-reference-remove absolute end-1 top-1 size-5 sm:size-5 [&_svg]:size-2.5 opacity-100 sm:opacity-0 focus-visible:opacity-100 focus-visible:outline-offset-1 group-hover:opacity-100"
          >
            <CloseIcon
              aria-hidden="true"
              focusable="false"
              weight="bold"
            />
          </IconButton>
        ) : null}
      </div>
      {tag ? (
        <figcaption className="flex max-w-full">
          <Badge tone="soft" className="max-w-full truncate px-1.5 py-0 text-[10px] font-medium leading-5">{tag}</Badge>
        </figcaption>
      ) : null}
    </figure>
  );
}
